import React, { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useDispatch, Provider, useSelector } from 'react-redux';
import store from './utils/store';
import { config } from './constants/config';
import { setUserId } from './slices/sseSlice';
import { getUserData } from './slices/userSlice';
import VoiceActorSystemInitializer from './components/VoiceActorSystemInitializer';
import { router } from './router';
import useIsLoggedIn from './isLoggedIn';
import SSEListener from './utils/SSEListener';
import BuildStamp from './components/BuildStamp';
import PreviewBanner from './components/PreviewBanner';
// Import debug controls for performance optimization
import './utils/debugControls';

/**
 * App Content Component
 * 
 * This component handles user initialization and renders the main app content.
 * It's separated from the main App component to access Redux state.
 */
function AppContent() {
  const dispatch = useDispatch();
  const token = useIsLoggedIn();
  const api_sse_endpoint = config.API_SSE_ENDPOINT;
  const userProfile = useSelector(state => state.user?.profile);
  const userId = userProfile?.data?.userId;
  const [userInitialized, setUserInitialized] = useState(false);

  // Initialize user data on app startup if user is logged in
  useEffect(() => {
    const initializeUser = async () => {
      if (token && !userProfile?.data) {
        try {
          console.log('App.jsx: Initializing user data...');
          await dispatch(getUserData({ token })).unwrap();
          console.log('App.jsx: User data initialized successfully');
        } catch (error) {
          console.error("App.jsx: Failed to initialize user data:", error);
        }
      }
      setUserInitialized(true);
    };

    initializeUser();
  }, [dispatch, token, userProfile?.data]);

  // Update userId in SSE state when it changes
  useEffect(() => {
    if (userId) {
      dispatch(setUserId(userId));
    }
  }, [dispatch, userId]);

  // Don't render router until user initialization is complete (if user is logged in)
  if (token && !userInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Initializing application...
      </div>
    );
  }

  return (
    <>
      {/*
        Preview-build courtesy strip. Renders null unless the build was
        made with REACT_APP_PREVIEW_BANNER=true (set by deploy-client.yml
        for prod). Mounted FIRST in the fragment so it occupies the top
        of <body> above the Navbar and every page route. The component
        is a single env-gated null check + a 28px strip — effectively
        free when disabled. See components/PreviewBanner.jsx header for
        the full design rationale.
      */}
      <PreviewBanner />

      {/* Pass userId to SSEListener */}
      {userId && <SSEListener url={`${api_sse_endpoint}?userId=${userId}`} />}

      {/* Initialize Voice Actor management system */}
      <VoiceActorSystemInitializer />

      {/*
        Provide the router.

        `future={{ v7_startTransition: true }}` opts into React Router v7's
        upcoming behavior of wrapping route state updates in
        `React.startTransition`. We're still on v6 today, so without this
        flag every cold load logs a console advisory:
          "React Router will begin wrapping state updates in
           React.startTransition in v7. You can use the v7_startTransition
           future flag to opt-in early."
        Opting in now (a) silences that advisory, and (b) gets us on the
        v7-compatible code path so the eventual upgrade is a no-op rather
        than a behavior change. See:
        https://reactrouter.com/v6/upgrading/future#v7_starttransition
      */}
      <RouterProvider
        router={router}
        future={{ v7_startTransition: true }}
      />

      {/*
        Build identifier (lower-right corner). Mounted *outside* the
        RouterProvider so it persists across every route — including
        login, error, and not-found pages where diagnosing "which
        bundle is the user actually running?" matters most. Bundle
        size impact is negligible (~1KB minified). See
        components/BuildStamp.jsx header for the rationale.
      */}
      <BuildStamp />
    </>
  );
}

/**
 * Main application component
 * 
 * This component initializes the Redux store and provides it to the app.
 */
function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
